import { useCallback } from "react"
import posthog from "posthog-js"
import type { ConnectorStatus } from "~/ui-rtk/shared/types/connectors"
import useCurrentCompany from "~/ui-rtk/hooks/current-company"

type ConnectorEventData = {
  connector_id?: string
  connector_name?: string
  service_name?: string
  connector_status?: ConnectorStatus | string
  is_airbyte?: boolean
  is_custom?: boolean
  is_beta?: boolean
  category?: string
  has_version_mismatch?: boolean
  is_created_by_migration?: boolean
  company_connector_id?: string
  button_text?: string
  search_term?: string
  connector_type?: "airbyte" | "fivetran" | "ingestion" | "custom"
  requires_pre_config?: boolean
  allow_multiple_connections?: boolean
  connection_index?: number
  is_healthy?: boolean
  is_super_admin?: boolean
  error_message?: string
}

const CONNECTOR_EVENTS = {
  CONNECT_CLICKED: "connectors_connect_clicked",
  SHOW_DETAILS_CLICKED: "connectors_show_details_clicked",
  RECONNECT_CLICKED: "connectors_reconnect_clicked",
  DISCONNECT_CLICKED: "connectors_disconnect_clicked",
  ADD_CONNECTION_CLICKED: "connectors_add_connection_clicked",
  CATEGORY_CHANGED: "connectors_category_changed",
  SEARCH: "connectors_search",
  REFRESH_CLICKED: "connectors_refresh_clicked",
  OAUTH_REDIRECT: "connectors_oauth_redirect",
  CONNECTION_SUCCESS: "connectors_connection_success",
  CONNECTION_FAILED: "connectors_connection_failed",
  RECONNECT_FAILED: "connectors_reconnect_failed",
  REMOVE_MIGRATION_RESTRICTIONS: "connectors_remove_migration_restrictions",
} as const

export function useConnectorsTracking() {
  const currentCompany = useCurrentCompany()

  const track = useCallback(
    (event: string, properties?: ConnectorEventData) => {
      posthog?.capture(event, {
        ...properties,
        company_id: currentCompany?.id,
        company_name: currentCompany?.name,
        $groups: { company: currentCompany?.id },
      })
    },
    [currentCompany?.id, currentCompany?.name],
  )

  const trackConnectClicked = useCallback(
    (data: ConnectorEventData) => {
      track(CONNECTOR_EVENTS.CONNECT_CLICKED, data)
    },
    [track],
  )

  const trackShowDetailsClicked = useCallback(
    (data: ConnectorEventData) => {
      track(CONNECTOR_EVENTS.SHOW_DETAILS_CLICKED, data)
    },
    [track],
  )

  const trackReconnectClicked = useCallback(
    (data: ConnectorEventData) => {
      track(CONNECTOR_EVENTS.RECONNECT_CLICKED, data)
    },
    [track],
  )

  const trackDisconnectClicked = useCallback(
    (data: ConnectorEventData) => {
      track(CONNECTOR_EVENTS.DISCONNECT_CLICKED, data)
    },
    [track],
  )

  const trackAddConnectionClicked = useCallback(
    (data: ConnectorEventData) => {
      track(CONNECTOR_EVENTS.ADD_CONNECTION_CLICKED, data)
    },
    [track],
  )

  const trackCategoryChanged = useCallback(
    (data: ConnectorEventData & { previous_category?: string }) => {
      track(CONNECTOR_EVENTS.CATEGORY_CHANGED, data)
    },
    [track],
  )

  const trackSearch = useCallback(
    (data: ConnectorEventData) => {
      track(CONNECTOR_EVENTS.SEARCH, data)
    },
    [track],
  )

  const trackRefreshClicked = useCallback(() => {
    track(CONNECTOR_EVENTS.REFRESH_CLICKED)
  }, [track])

  const trackOAuthRedirect = useCallback(
    (data: ConnectorEventData) => {
      track(CONNECTOR_EVENTS.OAUTH_REDIRECT, data)
    },
    [track],
  )

  const trackConnectionSuccess = useCallback(
    (data: ConnectorEventData) => {
      track(CONNECTOR_EVENTS.CONNECTION_SUCCESS, data)
    },
    [track],
  )

  const trackConnectionFailed = useCallback(
    (data: ConnectorEventData & { error_message?: string }) => {
      track(CONNECTOR_EVENTS.CONNECTION_FAILED, data)
    },
    [track],
  )

  const trackReconnectFailed = useCallback(
    (data: ConnectorEventData & { error_message?: string }) => {
      track(CONNECTOR_EVENTS.RECONNECT_FAILED, data)
    },
    [track],
  )

  const trackRemoveMigrationRestrictions = useCallback(
    (data: ConnectorEventData) => {
      track(CONNECTOR_EVENTS.REMOVE_MIGRATION_RESTRICTIONS, data)
    },
    [track],
  )

  return {
    trackConnectClicked,
    trackShowDetailsClicked,
    trackReconnectClicked,
    trackDisconnectClicked,
    trackAddConnectionClicked,
    trackCategoryChanged,
    trackSearch,
    trackRefreshClicked,
    trackOAuthRedirect,
    trackConnectionSuccess,
    trackConnectionFailed,
    trackReconnectFailed,
    trackRemoveMigrationRestrictions,
  }
}

export { CONNECTOR_EVENTS }
export type { ConnectorEventData }
